import React, { useState, useEffect } from "react";
import axios from "axios";
import { generateInvoiceHTML } from "./invoiceTemplate.js";
import "./batchPrintInvoices.css";

export default function InvoiceFilter() {
  // Helper function to get current month dates
  const getCurrentMonthDates = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return {
      start: formatDate(startDate),
      end: formatDate(endDate),
    };
  };

  const sortAccountsAlphabetically = (accounts) => {
    return [...accounts].sort((a, b) => {
      const nameA = a.account_name || "";
      const nameB = b.account_name || "";
      return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
    });
  };

  const defaultDates = getCurrentMonthDates();

  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);
  const [accounts, setAccounts] = useState("All");
  const [accountList, setAccountList] = useState([{ account_name: "All" }]);
  const [loading, setLoading] = useState(false);
  const [dateError, setDateError] = useState("");
  const [labInfo, setLabInfo] = useState(null);
  const [labId, setLabId] = useState(null);
  const [userType, setUserType] = useState(null);
  const [authLoaded, setAuthLoaded] = useState(false);

  const fetchLabInfo = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        console.error("No token found");
        return;
      }

      const response = await axios.get("https://hms.backend.hiragroupcloud.com/api/auth/users", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.users.length > 0) {
        setLabInfo(response.data.users[0]);
      }
    } catch (err) {
      console.error("Lab info fetch error:", err);
    }
  };

  useEffect(() => {
    let storedLabId = localStorage.getItem("userLabId");
    let storedUserType = localStorage.getItem("userType");

    if (!storedLabId) storedLabId = sessionStorage.getItem("userLabId");
    if (!storedUserType) storedUserType = sessionStorage.getItem("userType");

    const userDataStr =
      localStorage.getItem("userData") || sessionStorage.getItem("userData");
    if (userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);
        if (!storedLabId && userData.labId) storedLabId = userData.labId;
        if (!storedUserType && userData.userType) storedUserType = userData.userType;
      } catch (e) {
        console.error("Error parsing userData:", e);
      }
    }

    if (storedLabId) setLabId(storedLabId);

    if (storedUserType) {
      setUserType(storedUserType);
    } else if (storedLabId) {
      setUserType("lab");
    } else {
      setUserType("admin");
    }

    setAuthLoaded(true);
  }, []);

  useEffect(() => {
    const current = getCurrentMonthDates();
    setStartDate(current.start);
    setEndDate(current.end);
    fetchLabInfo();
  }, []);

  useEffect(() => {
    if (!authLoaded) return;
    if (userType === "lab" && !labId) return;

    const fetchAccounts = async () => {
      setLoading(true);
      try {
        let accountsArray = [];

        if (userType === "lab" && labId) {
          const res = await fetch(
            `https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${labId}`
          );
          const data = await res.json();

          if (data && data.success && Array.isArray(data.data)) {
            accountsArray = data.data;
          } else if (Array.isArray(data)) {
            accountsArray = data;
          } else if (Array.isArray(data?.accounts)) {
            accountsArray = data.accounts;
          }
        } else {
          const res = await fetch("https://hms.backend.hiragroupcloud.com/api/account/account");
          const data = await res.json();

          if (Array.isArray(data)) accountsArray = data;
          else if (Array.isArray(data.data)) accountsArray = data.data;
          else if (Array.isArray(data.accounts)) accountsArray = data.accounts;
        }

        accountsArray = accountsArray
          .filter((acc) => acc && (acc.account_name || acc.name))
          .map((acc) => ({
            ...acc,
            account_name: (acc.account_name || acc.name || "").trim(),
          }));

        const sortedAccounts = sortAccountsAlphabetically(accountsArray);
        setAccountList([{ account_name: "All" }, ...sortedAccounts]);
      } catch (err) {
        console.error("Error fetching accounts:", err);
        setAccountList([{ account_name: "All" }]);
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, [authLoaded, userType, labId]);

  const handleDateChange = (field, value) => {
    setDateError("");

    if (value === "") {
      if (field === "start") setStartDate(value);
      if (field === "end") setEndDate(value);
      return;
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      setDateError("Please select date using calendar icon");
      return;
    }

    if (field === "start") setStartDate(value);
    if (field === "end") setEndDate(value);
  };

  // ✅ Fetch invoices from backend (server-side filtered) and cases in parallel
  const fetchFilteredInvoices = async () => {
    const params = new URLSearchParams();

    if (labId) params.append("labId", labId);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (accounts && accounts !== "All") params.append("accountHolder", accounts);

    const url = `https://hms.backend.hiragroupcloud.com/api/invoices/filter?${params.toString()}`;

    const [invoicesRes, casesRes] = await Promise.all([
      fetch(url),
      fetch("https://hms.backend.hiragroupcloud.com/api/cases/cases"),
    ]);

    if (!invoicesRes.ok) {
      throw new Error(`Invoices request failed: ${invoicesRes.status}`);
    }

    const invoicesJson = await invoicesRes.json();

    // Handle multiple possible response shapes
    let invoices = [];
    if (Array.isArray(invoicesJson)) {
      invoices = invoicesJson;
    } else if (Array.isArray(invoicesJson.data)) {
      invoices = invoicesJson.data;
    } else if (Array.isArray(invoicesJson.invoices)) {
      invoices = invoicesJson.invoices;
    }

    // Cases — same defensive handling
    const casesJson = await casesRes.json();
    let cases = [];
    if (Array.isArray(casesJson)) cases = casesJson;
    else if (Array.isArray(casesJson.data)) cases = casesJson.data;
    else if (Array.isArray(casesJson.cases)) cases = casesJson.cases;

    return { invoices, cases };
  };

  const openInNewWindow = async () => {
    if (!startDate || !endDate) {
      alert("Please select both Start Date and End Date");
      return;
    }

    try {
      const { invoices, cases } = await fetchFilteredInvoices();

      if (!invoices || invoices.length === 0) {
        alert("No invoices found for the selected filters.");
        return;
      }

      // Build case map safely (caseId may be string or number)
      const caseMap = {};
      cases.forEach((c) => {
        if (c && c.case_id != null) {
          caseMap[String(c.case_id)] = c;
        }
      });

      const invoiceHtml = generateInvoiceHTML({
        invoices,
        accountList,
        caseMap,
        labInfo,
      });

      const newWin = window.open("", "_blank", "width=1000,height=800");
      if (!newWin) {
        alert("Please allow pop-ups for this site to print invoices.");
        return;
      }

      newWin.document.open();
      newWin.document.write(invoiceHtml);
      newWin.document.close();
    } catch (err) {
      console.error("Failed to load invoices:", err);
      alert(`Failed to load invoices: ${err.message}`);
    }
  };

  return (
    <div className="invoice-filter">
      <h3 className="invoice-heading">Parameters</h3>

      {dateError && <div className="invoice-date-error">{dateError}</div>}

      <div className="invoice-main-box">
        <div className="invoice-grid">
          <label className="invoice-label">
            Start Date <span className="invoice-required">*</span>
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => handleDateChange("start", e.target.value)}
            className="invoice-date-input"
          />

          <label className="invoice-label">
            End Date <span className="invoice-required">*</span>
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => handleDateChange("end", e.target.value)}
            className="invoice-date-input"
          />

          <label className="invoice-label">Accounts</label>
          <select
            name="accounts"
            value={accounts}
            onChange={(e) => setAccounts(e.target.value)}
            className="invoice-select"
            title={accounts}
          >
            {loading && accountList.length <= 1 ? (
              <option>Loading accounts...</option>
            ) : (
              accountList.map((acc, i) => (
                <option key={i} value={acc.account_name} title={acc.account_name}>
                  {acc.account_name}
                </option>
              ))
            )}
          </select>

          <label className="invoice-label">Account Tags</label>
          <input
            type="text"
            placeholder="No filter"
            className="invoice-tag-input"
          />
        </div>

        <div className="invoice-run-wrapper">
          <button onClick={openInNewWindow} className="invoice-run-button">
            <span className="invoice-run-icon">⌕</span>
            Run
          </button>
        </div>
      </div>
    </div>
  );
}